import { Fragment, type ReactNode } from 'react';

// Inline: **bold** and bare URLs.
const INLINE_RE = /(\*\*[^*]+\*\*|https?:\/\/[^\s)]+)/g;

function inline(text: string): ReactNode[] {
  return text.split(INLINE_RE).map((tok, i) => {
    if (/^\*\*[^*]+\*\*$/.test(tok)) {
      return (
        <strong key={i} className="font-semibold text-brand-navy">
          {tok.slice(2, -2)}
        </strong>
      );
    }
    if (/^https?:\/\//.test(tok)) {
      return (
        <a
          key={i}
          href={tok}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-brand-teal underline decoration-brand-teal/40 underline-offset-2 hover:decoration-brand-teal"
        >
          {tok}
        </a>
      );
    }
    return <Fragment key={i}>{tok}</Fragment>;
  });
}

// Lines that begin with one of these read as a labelled step / section heading.
const LABEL_RE =
  /^(STAGE\s*\d+[A-Z]?|STRETCH(?:\s*\([^)]*\))?|BEFORE YOU START|SUBMIT(?:\s*\([^)]*\))?|TEAM|COST|WHAT YOU['’]LL COVER|KEY TAKEAWAYS|NEXT STEPS|OVERVIEW|RECAP|OBJECTIVE|GOAL)\b/i;

/**
 * Render instructor-authored text (plain text or light markdown) with real
 * hierarchy — headings, labelled steps, bullet lists, bold, and links — so a
 * project/lab brief reads like a document instead of a wall of text.
 */
export function RichText({ text }: { text: string }) {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const nodes: ReactNode[] = [];
  let bullets: ReactNode[] = [];

  const flush = () => {
    if (bullets.length) {
      nodes.push(
        <ul key={`u${nodes.length}`} className="space-y-1.5">
          {bullets}
        </ul>,
      );
      bullets = [];
    }
  };

  lines.forEach((raw, i) => {
    const line = raw.trim();
    if (!line) {
      flush();
      return;
    }
    if (/^[-•*]\s+/.test(line)) {
      bullets.push(
        <li key={i} className="flex gap-2.5 text-[15px] leading-relaxed text-slate-600">
          <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-brand-teal/70" />
          <span>{inline(line.replace(/^[-•*]\s+/, ''))}</span>
        </li>,
      );
      return;
    }
    flush();

    const heading = line.match(/^(#{1,3})\s+(.*)$/);
    if (heading) {
      nodes.push(
        <h3 key={i} className="pt-3 text-base font-bold text-brand-navy">
          {inline(heading[2])}
        </h3>,
      );
      return;
    }

    if (LABEL_RE.test(line)) {
      const m = line.match(/^(.+?)(\s*[—–]\s+|:\s+)(.+)$/);
      if (m) {
        nodes.push(
          <p key={i} className="text-[15px] leading-relaxed text-slate-600">
            <span className="font-bold text-brand-navy">{m[1]}</span>
            <span className="text-slate-400"> — </span>
            {inline(m[3])}
          </p>,
        );
        return;
      }
      nodes.push(
        <p key={i} className="pt-1 text-[15px] font-bold text-brand-navy">
          {inline(line)}
        </p>,
      );
      return;
    }

    nodes.push(
      <p key={i} className="text-[15px] leading-relaxed text-slate-600">
        {inline(line)}
      </p>,
    );
  });
  flush();

  return <div className="space-y-2.5">{nodes}</div>;
}

/** First readable line of an instructions blob, for a card teaser. */
export function teaser(text: string, max = 150): string {
  const first = text
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((l) => l.trim())
    .find((l) => l && !/^[#>-]/.test(l));
  const clean = (first ?? '').replace(/\*\*/g, '');
  return clean.length > max ? `${clean.slice(0, max).trimEnd()}…` : clean;
}
